/**
 * API functions for user management and authentication
 */
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { authGeneration, sameAuthGeneration, isSupportToken } from './actorSupport';
// Base URL from environment variable or default to localhost

// Base URL from environment variable or default to localhost
const API_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_USER_SERVICE_URL) || 'http://localhost:8085';

// Validation responses can contain credential inputs. Read only their messages,
// never stringify or log the raw response, and tolerate non-JSON proxy errors.
const passwordResponseError = async (response, fallback) => {
  const data = await response.json().catch(() => null);
  return new Error(apiErrorMessage(data, `${fallback} (${response.status})`));
};

// ── Silent token refresh for raw fetch calls ──
let _fetchRefreshing = false;
let _fetchQueue = [];

const _processFetchQueue = (error, token = null) => {
  _fetchQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  _fetchQueue = [];
};

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token or throws.
 */
const _silentRefresh = async () => {
  const generation = authGeneration();
  const rt = localStorage.getItem('refreshToken');
  if (!rt) throw new Error('No refresh token');

  const resp = await fetch(`${API_URL}/api/v1/auth/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: rt }),
  });

  if (!resp.ok) throw new Error('Refresh failed');

  const data = await resp.json();
  if (!sameAuthGeneration(generation)) throw new Error('Die Anmeldung hat sich geändert.');
  localStorage.setItem('token', data.access_token);
  if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
  return data.access_token;
};

/**
 * Wrapper around fetch that automatically retries once with a refreshed token on 401.
 * Use this for any authenticated fetch call outside of axios.
 */
const fetchWithAuth = async (url, options = {}) => {
  const generation = authGeneration();
  const token = localStorage.getItem('token');
  const headers = { ...options.headers, Authorization: `Bearer ${token}` };
  const resp = await fetch(url, { ...options, headers });
  if (!sameAuthGeneration(generation)) throw new Error('Die Anmeldung hat sich geändert.');

  if (resp.status !== 401) return resp;

  // 401 — try silent refresh
  if (_fetchRefreshing) {
    // Another call is already refreshing; wait for it
    const newToken = await new Promise((resolve, reject) => {
      _fetchQueue.push({ resolve, reject });
    });
    return fetch(url, { ...options, headers: { ...options.headers, Authorization: `Bearer ${newToken}` } });
  }

  _fetchRefreshing = true;
  try {
    const newToken = await _silentRefresh();
    _processFetchQueue(null, newToken);
    return fetch(url, { ...options, headers: { ...options.headers, Authorization: `Bearer ${newToken}` } });
  } catch (err) {
    _processFetchQueue(err, null);
    if (!sameAuthGeneration(generation)) throw err;
    // Refresh truly failed — redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    if (window.location.pathname !== '/login') window.location.href = '/login';
    throw err;
  } finally {
    _fetchRefreshing = false;
  }
};

/**
 * Login user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} - Promise with login response
 */
const login = async (email, password) => {
  console.log('Login attempt for:', email);
  console.log('Login URL:', `${API_URL}/api/v1/auth/login`);
  
  try {
    const response = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        password: password
      }),
      credentials: 'include',
    });

    console.log('Login response status:', response.status);
    
    if (!response.ok) {
      // Try to parse error response as JSON
      try {
        const errorData = await response.json();
        console.error('Login error details:', errorData);
        
        // Handle specific error cases with clear messages
        if (response.status === 401) {
          throw new Error('Incorrect email or password. Please try again.');
        } else if (response.status === 403) {
          throw new Error(errorData.detail || 'Your account is not authorized to access the system.');
        } else {
          throw new Error(errorData.detail || `Login failed with status ${response.status}`);
        }
      } catch (parseError) {
        // If error response is not valid JSON or other parsing error
        console.error('Error parsing error response:', parseError);
        
        // Give specific messages based on status code even if JSON parsing failed
        if (response.status === 401) {
          throw new Error('Incorrect email or password. Please try again.');
        } else if (response.status === 403) {
          throw new Error('Your account is not authorized to access the system.');
        } else {
          throw new Error(`Login failed with status ${response.status}. Server might be down or having issues.`);
        }
      }
    }

    const data = await response.json();
    console.log('Login successful');
    console.log('Login response data:', JSON.stringify(data, null, 2));
    
    // Store the user's email for future reference
    localStorage.setItem('userEmail', email);
    
    // Check for user status information
    if (data.user && data.user.status) {
      console.log('User status:', data.user.status);
      
      // Special handling for different user statuses
      if (data.user.status === 'PENDING_PASSWORD_CHANGE') {
        console.log('User requires password change');
        localStorage.setItem('passwordChangeRequired', 'true');
        data.password_change_required = true;
      }
    }
    
    // Store tokens
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('refreshToken', data.refresh_token);

    // Normal login channel: full access according to the account's permissions
    localStorage.setItem('loginChannel', 'staff');

    // If password change required, store a flag to indicate this
    if (data.password_change_required) {
      console.log('Password change required, setting flag');
      localStorage.setItem('passwordChangeRequired', 'true');
    } else {
      localStorage.removeItem('passwordChangeRequired');
    }
    
    // Clear any passwordJustChanged flag
    localStorage.removeItem('passwordJustChanged');
    
    return {
      ...data,
      passwordChangeRequired: data.password_change_required
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Logout user
 * @returns {Promise} - Promise with logout response
 */
const logout = async () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return { message: 'Already logged out' };
  }
  
  const response = await fetch(`${API_URL}/api/v1/auth/logout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Logout failed');
  }

  // Clear token from localStorage
  localStorage.removeItem('token');
  
  return await response.json();
};

/**
 * Get current user information
 * @returns {Promise} - Promise with user info
 */
const setPreferredLanguage = async (preferredLanguage) => {
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/me/language`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preferred_language: preferredLanguage }),
  });
  if (!response.ok) throw new Error('Die Kontosprache konnte nicht gespeichert werden.');
  return response.json();
};

const setHeroPreferences = async (preferences) => {
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/me/hero-preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(preferences),
  });
  if (!response.ok) throw new Error('Die eScooter-Hero-Einstellungen konnten nicht gespeichert werden.');
  return response.json();
};

const getCurrentUser = async () => {
  console.log('Fetching current user info');
  
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  // Check if password change is required - if so, don't try to fetch user info
  const passwordChangeRequired = localStorage.getItem('passwordChangeRequired');
  if (passwordChangeRequired === 'true') {
    console.log('Password change required, skipping user info fetch');
    throw new Error('Password change required');
  }
  
  try {
    // Use fetchWithAuth which automatically refreshes token on 401
    const response = await fetchWithAuth(`${API_URL}/api/v1/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    console.log('Current user response status:', response.status);
    
    if (!response.ok) {
      // Try to parse error as JSON
      try {
        const errorData = await response.json();
        console.log('Error fetching user info:', errorData);
        
        // Special handling for common error cases
        if (response.status === 403) {
          if (errorData.detail && errorData.detail.includes('password change')) {
            localStorage.setItem('passwordChangeRequired', 'true');
            throw new Error('Your account requires a password change');
          }
        }
        
        throw new Error(`Failed to get user info (${response.status}): ${errorData.detail || 'Unknown error'}`);
      } catch (parseError) {
        console.log('Error parsing error response:', parseError);
        throw new Error(`Failed to get user info (${response.status})`);
      }
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in getCurrentUser:', error);
    
    // Special handling for CORS errors
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.log('CORS error detected, implementing fallback mechanism');
      
      // Check if we have a stored user ID in the token
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('Extracted user info from token:', payload);
          
          // Return minimal user info from token
          return {
            id: payload.sub,
            is_admin: payload.is_admin || false,
            roles: payload.roles || [],
            tags: payload.tags || [],
            actor_id: payload.actor_id || null,
            // We can't get the email from the token, so using a placeholder
            email: localStorage.getItem('userEmail') || 'user@example.com',
            // Other fields would be missing, but this covers the essentials
          };
        }
      } catch (tokenError) {
        console.error('Error extracting data from token:', tokenError);
      }
      
      // Check if password was just changed
      const passwordJustChanged = localStorage.getItem('passwordJustChanged');
      if (passwordJustChanged === 'true') {
        console.log('Password was just changed, clearing all tokens');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('passwordJustChanged');
        
        // Return a special error that will cause a redirect to login
        throw new Error('Session expired, please login again');
      }
    }
    
    throw error;
  }
};

/**
 * Change password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise with response
 */
const changePassword = async (currentPassword, newPassword) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/auth/password-change`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  if (!response.ok) {
    throw await passwordResponseError(response, 'Failed to change password');
  }

  return await response.json();
};

/**
 * Change password on first login
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise with response
 */
const firstLoginPasswordChange = async (newPassword) => {
  console.log('Changing password on first login');
  
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  try {
    const response = await fetchWithAuth(`${API_URL}/api/v1/auth/first-login-password-change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        new_password: newPassword
      }),
      credentials: 'include',
    });
    
    console.log('First login password change response status:', response.status);
    
    if (!response.ok) {
      throw await passwordResponseError(response, 'Password change failed');
    }
    
    // Set passwordJustChanged flag to trigger proper redirect logic
    localStorage.setItem('passwordJustChanged', 'true');
    
    // Clear the passwordChangeRequired flag
    localStorage.removeItem('passwordChangeRequired');
    
    console.log('First login password change successful');
    
    // No need to parse response data for password change, just return success
    return { success: true };
  } catch (error) {
    console.error('First login password change error:', error);
    throw error;
  }
};

/**
 * Refresh an access token using a refresh token
 * @param {string} refreshToken - The refresh token 
 * @returns {Promise} - Promise with new access token
 */
const refreshToken = async (rt) => {
  const response = await fetch(`${API_URL}/api/v1/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: rt }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Invalid or expired refresh token');
    }
    
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to refresh token');
  }

  const data = await response.json();
  // Store the rolling refresh token so the 30-day window resets
  if (data.refresh_token) {
    localStorage.setItem('refreshToken', data.refresh_token);
  }
  return data;
};

/**
 * Request a password reset
 * @param {string} email - User email
 * @returns {Promise} - Promise with response
 */
const requestPasswordReset = async (email) => {
  try {
    console.log('Requesting password reset for:', email);
    
    const response = await fetch(`${API_URL}/api/v1/auth/password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email
      }),
    });

    console.log('Password reset request response status:', response.status);
    
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        console.error('Password reset request error:', errorData);
        
        // Special handling for common error cases
        if (response.status === 401) {
          throw new Error('Invalid or expired refresh token');
        }
        
        errorMessage = errorData.detail || `Failed to request password reset (${response.status})`;
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
        errorMessage = `Failed to request password reset (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    console.log('Password reset request successful');
    return await response.json();
  } catch (error) {
    console.error('Password reset request error:', error);
    throw error;
  }
};

/**
 * Confirm password reset with token
 * @param {string} token - Reset token from email
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise with response
 */
const confirmPasswordReset = async (token, newPassword) => {
  try {
    console.log('Confirming password reset');
    
    const response = await fetch(`${API_URL}/api/v1/auth/password-reset-confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: token,
        new_password: newPassword
      }),
    });

    if (!response.ok) {
      throw await passwordResponseError(response, 'Failed to reset password');
    }

    return await response.json();
  } catch (error) {
    console.error('Password reset confirm error:', error);
    throw error;
  }
};

// Admin functions

/**
 * Get all users (Admin only)
 * @returns {Promise} - Promise with users list
 */
const getUsers = async (params = {}) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const query = new URLSearchParams();
  if (params.skip !== undefined) query.set('skip', params.skip);
  if (params.limit !== undefined) query.set('limit', params.limit);
  const queryString = query.toString() ? `?${query}` : '';
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/${queryString}`, {
    headers: {},
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get users');
  }

  return await response.json();
};

/**
 * Get user by ID (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise} - Promise with user info
 */
const getUserById = async (userId) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/${userId}`, {
    headers: {},
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get user');
  }

  return await response.json();
};

/**
 * Create user (Admin only)
 * @param {Object} userData - User data
 * @returns {Promise} - Promise with created user
 */
const createUser = async (userData) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to create user');
  }

  return await response.json();
};

/**
 * Update user (Admin only)
 * @param {string} userId - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise} - Promise with updated user
 */
const updateUser = async (userId, userData) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to update user');
  }

  return await response.json();
};

/**
 * Reset user password (admin only)
 * @param {string} userId - User ID to reset password for
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise with response
 */
const resetPassword = async (userId, newPassword) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  try {
    console.log(`Resetting password for user: ${userId}`);
    
    const response = await fetchWithAuth(`${API_URL}/api/v1/users/${userId}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      throw await passwordResponseError(response, 'Failed to reset password');
    }

    console.log('Password reset successful');
    return await response.json();
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

/**
 * Ban user (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise} - Promise with response
 */
const banUser = async (userId) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/${userId}/ban`, {
    method: 'PUT',
    headers: {},
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to ban user');
  }

  return await response.json();
};

/**
 * Unban user (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise} - Promise with response
 */
const unbanUser = async (userId) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/${userId}/unban`, {
    method: 'PUT',
    headers: {},
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to unban user');
  }

  return await response.json();
};

/**
 * Get audit logs (Admin only)
 * @param {Object} filters - Optional filters
 * @returns {Promise} - Promise with audit logs
 */
const getAuditLogs = async (filters = {}) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  if (filters.userId) {
    queryParams.append('user_id', filters.userId);
  }
  
  if (filters.actionType) {
    queryParams.append('action_type', filters.actionType);
  }
  
  if (filters.skip !== undefined) {
    queryParams.append('skip', filters.skip);
  }
  
  if (filters.limit !== undefined) {
    queryParams.append('limit', filters.limit);
  }
  
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  
  try {
    const response = await fetchWithAuth(`${API_URL}/api/v1/users/audit-logs${queryString}`, {
      headers: {},
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Not authorized to access audit logs');
      }
      
      if (response.status === 404) {
        console.warn('Audit logs endpoint not found, returning empty array');
        return [];
      }
      
      // For 422 Unprocessable Entity, return empty array
      if (response.status === 422) {
        console.warn('Unable to retrieve audit logs due to validation error. Returning empty list.');
        return [];
      }
      
      try {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to get audit logs (${response.status})`);
      } catch (e) {
        throw new Error(`Failed to get audit logs (${response.status})`);
      }
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    // Return empty array instead of throwing to prevent UI breakage
    return [];
  }
};

/**
 * Get login attempts (Admin only)
 * @param {Object} filters - Optional filters
 * @returns {Promise} - Promise with login attempts
 */
const getLoginAttempts = async (filters = {}) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  if (filters.email) {
    queryParams.append('email', filters.email);
  }
  
  if (filters.result) {
    queryParams.append('result', filters.result);
  }

  if (filters.search) {
    queryParams.append('search', filters.search);
  }

  if (filters.skip !== undefined) {
    queryParams.append('skip', filters.skip);
  }
  
  if (filters.limit !== undefined) {
    queryParams.append('limit', filters.limit);
  }
  
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/login-attempts${queryString}`, {
    headers: {},
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get login attempts');
  }

  return await response.json();
};

/**
 * Get current user permissions
 * @returns {Promise} - Promise with user permissions
 */
const getUserPermissions = async () => {
  const token = localStorage.getItem('token');
  if (isSupportToken(token)) {
    const current = await getCurrentUser();
    return { permissions: current.permissions.map((name) => ({ name, granted: true })), roles: current.roles, is_admin: false };
  }

  if (!token) {
    throw new Error('Not authenticated');
  }

  try {
    // First try to extract permissions from the JWT token as the primary method
    try {
      // Try to decode the JWT token to get roles
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('Extracted payload from token for permissions:', payload);

        // The real permissions are in the token, issued by user-management
        // from the person's tags. The page must show those, never a list
        // invented here from a role name.
        const granted = Array.isArray(payload.permissions) ? payload.permissions : [];
        const defaultPermissions = {
          permissions: granted.map((name) => ({ name: String(name), granted: true })),
          roles: Array.isArray(payload.roles) ? payload.roles : [],
          is_admin: payload.is_admin === true,
        };

        // Return the default permissions
        return defaultPermissions;
      }
    } catch (tokenError) {
      console.error('Error extracting permissions from token:', tokenError);
      // Continue to API fallback if token extraction fails
    }

    // Only if token extraction fails, try API
    console.log('Token permissions extraction failed, falling back to API request');
    const response = await fetchWithAuth(`${API_URL}/api/v1/users/permissions`, {
      headers: {},
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication expired');
      }

      // Only reach here if both methods failed
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get user permissions' }));
      throw new Error(errorData.detail || 'Failed to get user permissions');
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error('Error fetching permissions:', err);
    throw err;
  }
};

/**
 * Sync roles with tags from tag management service
 * @returns {Promise} - Promise with sync response
 */
const syncRolesWithTags = async () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/sync-roles-with-tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to sync roles with tags');
  }
  
  return await response.json();
};

/**
 * Get available actors for conversion to users (Admin only) - paginated
 * @param {Object} params - { skip, limit, search }
 * @returns {Promise} - Promise with { items: [...], total: N }
 */
const getAvailableActorsForUsers = async (params = {}) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const queryParams = new URLSearchParams();
  if (params.skip !== undefined) queryParams.append('skip', params.skip);
  if (params.limit !== undefined) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/actor-users/available-actors${queryString}`, {
    headers: {},
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Not authorized to access this resource');
    }
    
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to get available actors');
  }
  
  return await response.json();
};

/**
 * Convert actor to user (Admin only)
 * @param {string} actorId - Actor ID
 * @param {string} password - Initial password for the user
 * @param {Array} roles - Array of role names to assign
 * @param {boolean} isAdmin - Whether the user should have admin privileges
 * @returns {Promise} - Promise with created user
 */
const convertActorToUser = async (actorId, password, roles = [], isAdmin = false) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  let url = `${API_URL}/api/v1/actor-users/convert-actor/${actorId}?password=${encodeURIComponent(password)}&is_admin=${isAdmin}`;
  
  // Add roles as query parameters if provided
  if (roles && roles.length > 0) {
    roles.forEach(role => {
      url += `&roles=${encodeURIComponent(role)}`;
    });
  }
  
  const response = await fetchWithAuth(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Not authorized to perform this action');
    }
    
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to convert actor to user');
  }
  
  return await response.json();
};

/**
 * Get user roles (including roles from actor tags)
 * @returns {Promise<Array>} List of roles
 */
const getRoles = async () => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  try {
    const response = await fetchWithAuth(`${API_URL}/api/v1/users/roles`, {
      headers: {},
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Not authorized to access this resource');
      }
      
      // For 422 Unprocessable Entity or 401 Unauthorized, we'll just return an empty array
      // This happens when the user doesn't have an actor or the actor has no tags
      // or when there's an issue with authentication
      if (response.status === 422 || response.status === 401) {
        console.warn(`Unable to retrieve roles due to ${response.status === 422 ? 'validation error' : 'authentication issue'}. Returning empty list.`);
        return [];
      }
      
      try {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to get roles: ${response.status}`);
      } catch (e) {
        // If we can't parse the error as JSON, throw a generic error
        throw new Error(`Failed to get roles: ${response.status}`);
      }
    }
    
    const data = await response.json();
    
    // Check if roles property exists and is an array
    if (data && data.roles && Array.isArray(data.roles)) {
      return data.roles;
    }
    
    // If response format is unexpected, log warning and return empty array
    console.warn('Unexpected response format from roles endpoint:', data);
    return [];
  } catch (error) {
    console.error('Error in getRoles:', error);
    // Return empty array on error rather than throwing
    return [];
  }
};

/**
 * Sync user roles with actor tags
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Sync result
 */
const syncUserRolesWithTags = async (userId) => {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/${userId}/sync-roles-with-tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Not authorized to perform this action');
    }
    
    try {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to sync roles with tags');
    } catch (e) {
      throw new Error('Failed to sync roles with tags');
    }
  }
  
  return await response.json();
};

/**
 * Get authentication token from local storage
 * @returns {string|null} Authentication token or null if not found
 */
const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * Upload profile picture for the current user
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise} - Promise with upload response
 */
const uploadProfilePicture = async (formData) => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Log the form data for debugging
    console.log('Uploading profile picture with form data:', formData.get('file'));
    
    // Use the proper API endpoint that matches the backend
    const response = await fetchWithAuth(`${API_URL}/api/v1/users/me/profile-picture`, {
      method: 'POST',
      headers: {
        // Do NOT set Content-Type here, let the browser set it with boundary for FormData
      },
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Profile picture upload failed:', errorData);
      throw new Error(errorData.detail || errorData.message || `Upload failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    throw error;
  }
};

/**
 * Delete profile picture for the current user
 * @returns {Promise} - Promise with delete response
 */
const deleteProfilePicture = async () => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetchWithAuth(`${API_URL}/api/v1/users/me/profile-picture`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Profile picture deletion failed:', errorData);
      throw new Error(errorData.detail || errorData.message || `Deletion failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting profile picture:', error);
    throw error;
  }
};

/**
 * Delete user (Admin only)
 * @param {string} userId - User ID
 * @returns {Promise} - Promise with response
 */
const deleteUser = async (userId) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const response = await fetchWithAuth(`${API_URL}/api/v1/users/${userId}`, {
    method: 'DELETE',
    headers: {},
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to delete user');
  }

  return await response.json();
};

// Export API functions
export default {
  login,
  logout,
  getCurrentUser,
  setPreferredLanguage,
  setHeroPreferences,
  changePassword,
  firstLoginPasswordChange,
  refreshToken,
  requestPasswordReset,
  confirmPasswordReset,
  getUsers,
  getUserById,
  createUser,
  updateUser,
  resetPassword,
  banUser,
  unbanUser,
  deleteUser,
  getAuditLogs,
  getLoginAttempts,
  getUserPermissions,
  syncRolesWithTags,
  getAvailableActorsForUsers,
  convertActorToUser,
  getRoles,
  syncUserRolesWithTags,
  getToken,
  uploadProfilePicture,
  deleteProfilePicture,
  requestCustomerOtp,
  verifyCustomerOtp,
  verifyLoginCode
};


async function requestCustomerOtp(identifier) {
  const response = await fetch(`${API_URL}/api/v1/auth/customer/request-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier })
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to request verification code');
  }
  return response.json();
}


async function verifyCustomerOtp(identifier, code) {
  const response = await fetch(`${API_URL}/api/v1/auth/customer/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, code })
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Invalid or expired verification code');
  }
  return response.json();
}


// Passwordless login for ANY existing account (staff or customer) via OTP.
// Unlike verifyCustomerOtp, this returns a FULL session (real roles/permissions)
// and stores it exactly like the password login does.
async function verifyLoginCode(identifier, code) {
  const response = await fetch(`${API_URL}/api/v1/auth/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, code })
  });
  if (!response.ok) {
    let detail = 'Invalid or expired verification code';
    try { detail = (await response.json()).detail || detail; } catch (_) { /* ignore */ }
    if (response.status === 404) {
      detail = 'No account found for this email. Use password login or contact your administrator.';
    }
    throw new Error(detail);
  }
  const data = await response.json();

  // Store the session the same way password login does — full staff access.
  localStorage.setItem('userEmail', identifier);
  localStorage.setItem('token', data.access_token);
  localStorage.setItem('refreshToken', data.refresh_token);
  localStorage.setItem('loginChannel', 'staff');
  if (Array.isArray(data.permissions)) {
    localStorage.setItem('userPermissions', JSON.stringify(data.permissions));
  }
  if (data.is_admin !== undefined) {
    localStorage.setItem('isAdmin', JSON.stringify(data.is_admin));
  }
  if (data.password_change_required) {
    localStorage.setItem('passwordChangeRequired', 'true');
  } else {
    localStorage.removeItem('passwordChangeRequired');
  }
  localStorage.removeItem('passwordJustChanged');

  return { ...data, passwordChangeRequired: data.password_change_required };
}

